(() => {
  const STORAGE_KEY = 'cqz_state';

  const initInlineForm = (formWrap) => {
    if (!formWrap || formWrap._cqzFormInit) return;
    formWrap._cqzFormInit = true;

    const form = formWrap.querySelector('[data-cqz-form]');
    const emailInput = formWrap.querySelector('[data-cqz-form-email]');
    const submitBtn = formWrap.querySelector('[data-cqz-form-submit]');
    const errorEl = formWrap.querySelector('[data-cqz-form-error]');
    const companyId = formWrap.dataset.cqzCompanyId || '';
    const redirectUrl = formWrap.dataset.cqzRedirect || '/';

    if (!form || !emailInput || !submitBtn) return;

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const showError = (msg) => {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.removeAttribute('hidden');
      emailInput.setAttribute('aria-invalid', 'true');
      if (errorEl.id) emailInput.setAttribute('aria-describedby', errorEl.id);
    };

    const clearError = () => {
      if (!errorEl) return;
      errorEl.textContent = '';
      errorEl.setAttribute('hidden', '');
      emailInput.classList.remove('is-error');
      emailInput.removeAttribute('aria-invalid');
      emailInput.removeAttribute('aria-describedby');
    };

    const readAnswers = () => {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const s = JSON.parse(raw);
        return Array.isArray(s.answers) ? s.answers : null;
      } catch (_) {
        return null;
      }
    };

    const submitToKlaviyo = async (email) => {
      if (!companyId) {
        throw new Error('Klaviyo company ID is not set. Add it in theme editor.');
      }
      const answers = readAnswers();
      const customProps = { quiz_source: 'Quiz' };
      if (answers) {
        answers.forEach((value, idx) => {
          if (value) customProps['quiz_q' + (idx + 1)] = value;
        });
      }
      const payload = {
        data: {
          type: 'profile',
          attributes: { email: email, properties: customProps },
        },
      };
      const url = 'https://a.klaviyo.com/client/profiles/?company_id=' + encodeURIComponent(companyId);
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify(payload),
      });
      if (res.status === 409) {
        let existingId = '';
        try {
          const body = await res.json();
          existingId = (body && body.errors && body.errors[0] && body.errors[0].meta && body.errors[0].meta.duplicate_profile_id) || '';
        } catch (_) {}
        if (existingId) {
          const patchPayload = {
            data: {
              type: 'profile',
              id: existingId,
              attributes: { properties: customProps },
            },
          };
          const patchUrl = 'https://a.klaviyo.com/client/profiles/' + encodeURIComponent(existingId) + '/?company_id=' + encodeURIComponent(companyId);
          await fetch(patchUrl, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
            body: JSON.stringify(patchPayload),
          });
        }
        return;
      }
      if (!res.ok && res.status !== 202) {
        let detail = '';
        try {
          const body = await res.json();
          detail = (body && body.errors && body.errors[0] && body.errors[0].detail) || '';
        } catch (_) {}
        throw new Error(detail || 'Subscription failed (' + res.status + ').');
      }
    };

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      clearError();
      const email = (emailInput.value || '').trim();
      if (!email || !isValidEmail(email)) {
        emailInput.classList.add('is-error');
        showError('Please enter a valid email address.');
        emailInput.focus();
        return;
      }
      submitBtn.classList.add('is-loading');
      submitBtn.disabled = true;
      try {
        await submitToKlaviyo(email);
        try {
          sessionStorage.removeItem(STORAGE_KEY);
        } catch (_) {}
        window.location.href = redirectUrl;
      } catch (err) {
        showError(err.message || 'Something went wrong. Please try again.');
        submitBtn.classList.remove('is-loading');
        submitBtn.disabled = false;
      }
    });
  };

  const startInlineLoading = (loadingEl) => {
    if (!loadingEl || loadingEl._cqzLoadingRunning) return;
    loadingEl._cqzLoadingRunning = true;

    const duration = parseInt(loadingEl.dataset.cqzDuration, 10) || 4000;
    const ring = loadingEl.querySelector('[data-cqz-ring]');
    const pctEl = loadingEl.querySelector('[data-cqz-pct]');
    let length = 465;
    if (ring) {
      try {
        if (ring.getTotalLength) {
          const m = ring.getTotalLength();
          if (m > 0) length = m;
        }
        ring.style.strokeDasharray = String(length);
        ring.style.strokeDashoffset = String(length);
      } catch (_) {}
    }

    const setPct = (pct) => {
      const clamped = Math.max(0, Math.min(100, pct));
      if (pctEl) pctEl.textContent = Math.round(clamped) + '%';
      if (ring) ring.style.strokeDashoffset = String(length * (1 - clamped / 100));
    };

    setPct(0);
    const startedAt = performance.now();
    const tick = (now) => {
      const elapsed = now - startedAt;
      const pct = Math.min(100, (elapsed / duration) * 100);
      setPct(pct);
      if (pct < 100) {
        requestAnimationFrame(tick);
      } else {
        window.setTimeout(() => {
          document.dispatchEvent(new CustomEvent('quiz:loading-complete'));
          const formEl = document.querySelector('[data-cqz-form-wrap]');
          if (formEl) {
            formEl.removeAttribute('hidden');
            formEl.classList.add('is-active');
            formEl.style.display = 'flex';
            loadingEl.classList.remove('is-active');
            loadingEl.setAttribute('hidden', '');
            loadingEl.style.display = 'none';
            initInlineForm(formEl);
            window.scrollTo(0, 0);
          }
        }, 400);
      }
    };
    requestAnimationFrame(tick);
  };

  const readState = () => {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || { answers: [], current: 0 };
    } catch (_) {
      return { answers: [], current: 0 };
    }
  };

  const writeState = (state) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_) {}
  };

  const initSection = (root) => {
    if (!root || root._cqzInit) return;
    root._cqzInit = true;

    const total = parseInt(root.dataset.cqzTotal, 10) || 0;
    if (total === 0) return;

    const stage = root.querySelector('[data-cqz-stage]');
    const steps = Array.from(root.querySelectorAll('[data-cqz-step]'));
    const markers = Array.from(root.querySelectorAll('[data-cqz-progress-marker]'));
    const progressFill = root.querySelector('[data-cqz-progress-fill]');
    const progressEl = root.querySelector('[data-cqz-progress]');
    const progressEndDot = root.querySelector('[data-cqz-progress-end]');
    const backBtn = root.querySelector('[data-cqz-back]');
    const nextBtn = root.querySelector('[data-cqz-next]');
    const nextLabel = root.querySelector('[data-cqz-next-label]');

    const nextText = root.dataset.cqzNextText || 'Next';
    const gotItText = root.dataset.cqzGotItText || 'Got it';

    const state = readState();
    if (state.current >= total) state.current = 0;
    let current = state.current;
    const answers = Array.isArray(state.answers) ? state.answers : [];

    const updateProgress = () => {
      const stepNum = current + 1;
      const pct = Math.min(100, Math.max(0, (stepNum / total) * 100));
      if (progressFill) progressFill.style.width = pct + '%';
      if (progressEl) {
        progressEl.setAttribute('aria-valuenow', String(Math.round(pct)));
        progressEl.setAttribute('aria-valuetext', 'Step ' + stepNum + ' of ' + total);
      }
      markers.forEach((el) => {
        const pos = parseFloat(el.getAttribute('data-cqz-progress-marker')) * 25;
        el.classList.remove('is-active', 'is-done');
        if (pct >= pos) el.classList.add('is-done');
      });
      if (progressEndDot) {
        progressEndDot.classList.toggle('is-done', pct >= 100);
      }
    };

    let stepShown = false;

    const showStep = (idx) => {
      steps.forEach((stepEl, i) => {
        stepEl.classList.toggle('is-active', i === idx);
      });
      const heading = steps[idx] ? steps[idx].querySelector('h1, h2') : null;
      if (stepShown && heading) {
        heading.setAttribute('tabindex', '-1');
        heading.style.outline = 'none';
        heading.focus({ preventScroll: true });
      }
      stepShown = true;
    };

    const refreshNextButton = () => {
      const stepEl = steps[current];
      if (!stepEl) return;
      const type = stepEl.dataset.cqzStepType;
      if (type === 'insight') {
        nextBtn.classList.remove('is-disabled');
        nextBtn.disabled = false;
        if (nextLabel) nextLabel.textContent = gotItText;
      } else if (type === 'question') {
        if (nextLabel) nextLabel.textContent = nextText;
        const selected = stepEl.querySelector('[data-cqz-option].is-selected');
        if (selected) {
          nextBtn.classList.remove('is-disabled');
          nextBtn.disabled = false;
        } else {
          nextBtn.classList.add('is-disabled');
          nextBtn.disabled = true;
        }
      } else {
        nextBtn.classList.remove('is-disabled');
        nextBtn.disabled = false;
        if (nextLabel) nextLabel.textContent = nextText;
      }
    };

    const refreshBackButton = () => {
      backBtn.hidden = current === 0;
    };

    const saveState = () => {
      writeState({ answers, current });
    };

    const applySavedAnswers = () => {
      answers.forEach((value, idx) => {
        const stepEl = steps[idx];
        if (!stepEl || value == null) return;
        const opts = stepEl.querySelectorAll('[data-cqz-option]');
        opts.forEach((opt) => {
          if (opt.dataset.cqzOptionValue === value) {
            opt.classList.add('is-selected');
            opt.setAttribute('aria-pressed', 'true');
          }
        });
      });
    };

    const handleOptionClick = (e) => {
      const opt = e.target.closest('[data-cqz-option]');
      if (!opt) return;
      const stepEl = opt.closest('[data-cqz-step]');
      if (!stepEl) return;
      stepEl.querySelectorAll('[data-cqz-option].is-selected').forEach((el) => {
        if (el !== opt) {
          el.classList.remove('is-selected');
          el.setAttribute('aria-pressed', 'false');
        }
      });
      opt.classList.add('is-selected');
      opt.setAttribute('aria-pressed', 'true');
      const idx = parseInt(stepEl.dataset.cqzStep, 10);
      answers[idx] = opt.dataset.cqzOptionValue;
      saveState();
      refreshNextButton();
    };

    const handleNext = () => {
      const stepEl = steps[current];
      if (!stepEl) return;
      const type = stepEl.dataset.cqzStepType;
      if (type === 'question') {
        const selected = stepEl.querySelector('[data-cqz-option].is-selected');
        if (!selected) return;
      }

      if (current >= total - 1) {
        saveState();
        const payload = steps.map((stepEl, idx) => {
          return {
            index: idx,
            type: stepEl.dataset.cqzStepType,
            value: answers[idx] || null,
          };
        });
        document.dispatchEvent(new CustomEvent('quiz:complete', { detail: { answers: payload } }));

        const loadingEl = root.querySelector('[data-cqz-loading]');
        if (loadingEl) {
          loadingEl.removeAttribute('hidden');
          loadingEl.classList.add('is-active');
          startInlineLoading(loadingEl);
        }

        root.setAttribute('data-cqz-done', 'true');
        const stepInner = root.querySelector('.cqz-i');
        if (stepInner) stepInner.style.display = 'none';
        window.scrollTo(0, 0);
        return;
      }

      current = current + 1;
      saveState();
      showStep(current);
      updateProgress();
      refreshNextButton();
      refreshBackButton();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleBack = () => {
      if (current === 0) return;
      current = current - 1;
      saveState();
      showStep(current);
      updateProgress();
      refreshNextButton();
      refreshBackButton();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    stage.addEventListener('click', handleOptionClick);
    nextBtn.addEventListener('click', handleNext);
    backBtn.addEventListener('click', handleBack);

    applySavedAnswers();
    showStep(current);
    updateProgress();
    refreshNextButton();
    refreshBackButton();
  };

  const init = () => {
    document.querySelectorAll('[data-cqz-step-section]').forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
